import { useState, useCallback, useEffect, useMemo } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useAppStore } from '../../state/store';
import { GROUND_PLANE } from '../../core/scene';
import { constrainDraftPoint } from '../snapping';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { undoableAdd, makeEntity } from '../../core/commands/entityOps';

/**
 * Line Tool Hook - 2D-style line drawing
 * 
 * Replicates 2D LINE command:
 * - Click to place first point
 * - Move to define endpoint
 * - Click to place (continues for polyline)
 * - Enter/Escape to finish
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useLineTool() {
    const [isDrawing, setIsDrawing] = useState(false);
    const [points, setPoints] = useState<THREE.Vector3[]>([]);
    const [currentPoint, setCurrentPoint] = useState<THREE.Vector3 | null>(null);
    // Shared global Ortho state (F8 / F1 in CAD & 3D workbenches, toolbar).
    const orthoMode = useAppStore((s) => s.orthoMode);

    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'line';

    /**
     * Get point on ground plane (canonical Z-up frame, see core/scene/axes)
     */
    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    /**
     * Shared drafting constraint pipeline: grid snap (F9) → osnap (F3) →
     * ortho (F8) against the last committed point.
     */
    const constrain = useCallback((point: THREE.Vector3, base?: THREE.Vector3 | null): THREE.Vector3 => {
        return constrainDraftPoint(point, camera, gl, base);
    }, [camera, gl]);

    /**
     * Create line entity — undoable (Ctrl+Z reverts freshly drawn geometry).
     */
    const createLine = useCallback((start: THREE.Vector3, end: THREE.Vector3) => {
        // No explicit color: rendering resolves the theme ink (white on the
        // dark viewport, black on the light one) as the default line color.
        undoableAdd([makeEntity('line', `Line-${Date.now()}`, {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
        }, activeLayerId)]);
    }, [activeLayerId]);

    /**
     * Handle pointer down
     */
    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const base = points.length > 0 ? points[points.length - 1] : null;
        const snapped = constrain(point, base);

        if (base) {
            createLine(base, snapped);
        }

        setPoints([...points, snapped]);
        setIsDrawing(true);
    }, [isActive, points, getGroundPoint, constrain, createLine]);

    /**
     * Handle pointer move
     */
    const handlePointerMove = useCallback(() => {
        if (!isActive || !isDrawing || points.length === 0) return;

        const point = getGroundPoint();
        if (point) {
            const snapped = constrain(point, points[points.length - 1]);
            // Snapped cursor: identical writes must not re-render the tool scope.
            setCurrentPoint((prev) => (prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped));
        }
    }, [isActive, isDrawing, points, getGroundPoint, constrain]);

    /**
     * Handle keyboard
     */
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.key === 'Enter') {
                setIsDrawing(false);
                setPoints([]);
                setCurrentPoint(null);
            }
            // F8/F1 ortho is handled globally (KeyboardListener + command
            // line) so the toggle never fires twice.
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return {
        isActive,
        isDrawing,
        points,
        currentPoint,
        orthoMode,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Line Preview Component
 */
export function LinePreview({
    from,
    to
}: {
    from: THREE.Vector3;
    to: THREE.Vector3;
}) {
    return (
        <Line
            points={[from, to]}
            color="#00ff00"
            lineWidth={2}
            dashed
            dashSize={0.2}
            gapSize={0.1}
        />
    );
}

/**
 * Circle Tool Hook - 2D-style circle drawing
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCircleTool() {
    const [center, setCenter] = useState<THREE.Vector3 | null>(null);
    const [radius, setRadius] = useState(0);
    const [isDrawing, setIsDrawing] = useState(false);

    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'circle';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const createCircle = useCallback((centerPt: THREE.Vector3, r: number) => {
        undoableAdd([makeEntity('circle', `Circle-${Date.now()}`, {
            center: [centerPt.x, centerPt.y, centerPt.z],
            radius: r,
            segments: 64,
        }, activeLayerId)]);
    }, [activeLayerId]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;
        const constrained = constrainDraftPoint(point, camera, gl, isDrawing ? center : null);

        if (!isDrawing) {
            setCenter(constrained);
            setIsDrawing(true);
        } else if (center) {
            const r = center.distanceTo(constrained);
            if (r > 0.1) {
                createCircle(center, r);
            }
            setIsDrawing(false);
            setCenter(null);
            setRadius(0);
        }
    }, [isActive, isDrawing, center, getGroundPoint, createCircle, camera, gl]);

    const handlePointerMove = useCallback(() => {
        if (!isActive || !isDrawing || !center) return;

        const point = getGroundPoint();
        if (point) {
            const next = center.distanceTo(constrainDraftPoint(point, camera, gl, center));
            // Radius is continuous (unsnapped): skip sub-0.1mm jitter writes
            // so the preview re-renders only on real movement.
            setRadius((prev) => (Math.abs(prev - next) < 1e-4 ? prev : next));
        }
    }, [isActive, isDrawing, center, getGroundPoint, camera, gl]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;
            if (e.key === 'Escape') {
                setIsDrawing(false);
                setCenter(null);
                setRadius(0);
            } else if (e.key === 'Enter' && isDrawing && center && radius > 0.1) {
                // Enter commits the circle with the live preview radius
                // (same completion contract as the Line tool).
                createCircle(center, radius);
                setIsDrawing(false);
                setCenter(null);
                setRadius(0);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, isDrawing, center, radius, createCircle]);

    return {
        isActive,
        isDrawing,
        center,
        radius,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Circle Preview Component
 */
export function CirclePreview({
    center,
    radius
}: {
    center: THREE.Vector3;
    radius: number;
}) {
    if (radius < 0.01) return null;

    // Create circle points on the XY ground plane (Z-up convention)
    const segments = 64;
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        points.push(new THREE.Vector3(
            center.x + Math.cos(angle) * radius,
            center.y + Math.sin(angle) * radius,
            center.z
        ));
    }

    return (
        <Line
            points={points}
            color="#00ff00"
            lineWidth={2}
            dashed
            dashSize={0.2}
            gapSize={0.1}
        />
    );
}

/**
 * Rectangle Tool Hook - 2D-style rectangle drawing
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRectangleTool() {
    const [corner1, setCorner1] = useState<THREE.Vector3 | null>(null);
    const [corner2, setCorner2] = useState<THREE.Vector3 | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'rectangle';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const createRectangle = useCallback((c1: THREE.Vector3, c2: THREE.Vector3) => {
        // FILLETRADIUS sysvar: a positive radius rounds the four corners
        // (classic RECTANG Fillet option) by emitting a bulged polyline.
        const fr = useSystemVarsStore.getState().FILLETRADIUS ?? 0;
        if (fr > 0.001) {
            const x0 = Math.min(c1.x, c2.x), x1 = Math.max(c1.x, c2.x);
            const y0 = Math.min(c1.y, c2.y), y1 = Math.max(c1.y, c2.y);
            const r = Math.min(fr, (x1 - x0) / 2 - 1e-4, (y1 - y0) / 2 - 1e-4);
            if (r > 1e-4) {
                const corners: [number, number][] = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
                const verts: number[][] = [];
                const bulges: number[] = [];
                const half = r / Math.SQRT2;
                const bulgeVal = Math.tan((Math.PI / 2) / 4); // 90-deg arc
                for (let i = 0; i < 4; i++) {
                    const [cx, cy] = corners[i];
                    const [px, py] = corners[(i + 3) % 4];
                    const [nx, ny] = corners[(i + 1) % 4];
                    const d1 = Math.hypot(cx - px, cy - py) || 1;
                    const d2 = Math.hypot(nx - cx, ny - cy) || 1;
                    const a: [number, number] = [cx + (px - cx) / d1 * half, cy + (py - cy) / d1 * half];
                    const b: [number, number] = [cx + (nx - cx) / d2 * half, cy + (ny - cy) / d2 * half];
                    verts.push([a[0], a[1], c1.z]);
                    bulges.push(bulgeVal);
                    verts.push([b[0], b[1], c1.z]);
                    bulges.push(0);
                }
                undoableAdd([makeEntity('polyline', `Rectangle-${Date.now()}`, {
                    vertices: verts, bulges, closed: true, rectFillet: r,
                }, activeLayerId)]);
                return;
            }
        }
        undoableAdd([makeEntity('rectangle', `Rectangle-${Date.now()}`, {
            corner1: [c1.x, c1.y, c1.z],
            corner2: [c2.x, c2.y, c2.z],
        }, activeLayerId)]);
    }, [activeLayerId]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const raw = getGroundPoint();
        if (!raw) return;
        const point = constrainDraftPoint(raw, camera, gl, isDrawing ? corner1 : null);

        if (!isDrawing) {
            setCorner1(point);
            setCorner2(point);
            setIsDrawing(true);
        } else if (corner1) {
            createRectangle(corner1, point);
            setIsDrawing(false);
            setCorner1(null);
            setCorner2(null);
        }
    }, [isActive, isDrawing, corner1, getGroundPoint, createRectangle, camera, gl]);

    const handlePointerMove = useCallback(() => {
        if (!isActive || !isDrawing) return;

        const point = getGroundPoint();
        if (point) {
            const snapped = constrainDraftPoint(point, camera, gl, corner1);
            setCorner2((prev) => (prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped));
        }
    }, [isActive, isDrawing, getGroundPoint, camera, gl, corner1]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsDrawing(false);
                setCorner1(null);
                setCorner2(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return {
        isActive,
        isDrawing,
        corner1,
        corner2,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Rectangle Preview Component
 */
export function RectanglePreview({
    corner1,
    corner2
}: {
    corner1: THREE.Vector3;
    corner2: THREE.Vector3;
}) {
    const points = [
        new THREE.Vector3(corner1.x, corner1.y, corner1.z),
        new THREE.Vector3(corner2.x, corner1.y, corner1.z),
        new THREE.Vector3(corner2.x, corner2.y, corner1.z),
        new THREE.Vector3(corner1.x, corner2.y, corner1.z),
        new THREE.Vector3(corner1.x, corner1.y, corner1.z),
    ];

    return (
        <Line
            points={points}
            color="#00ff00"
            lineWidth={2}
            dashed
            dashSize={0.2}
            gapSize={0.1}
        />
    );
}
